import React, { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Upload, Trash2, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import api from '@/services/api';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export const PdfSlideViewer = ({ code, pdfSlides, isAdmin, onPdfChange }) => {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(true);

  useEffect(() => {
    if (pdfSlides) {
      loadPdf();
    }
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [pdfSlides]);

  const loadPdf = async () => {
    setLoadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/training/modules/${code}/pdf`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const blobUrl = URL.createObjectURL(res.data);
      setPdfUrl(blobUrl);
    } catch {
      toast.error('Failed to load PDF');
    } finally {
      setLoadingPdf(false);
    }
  };

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setCurrentPage(1);
  }, []);

  if (loadingPdf) {
    return (
      <Card className="border-2 border-gray-200">
        <CardContent className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600 mr-2" />
          <span className="text-sm text-muted-foreground">Loading PDF slides...</span>
        </CardContent>
      </Card>
    );
  }

  if (!pdfUrl) return null;

  return (
    <div className="space-y-3" data-testid="pdf-slide-viewer">
      <Card className="border-2 border-indigo-100 overflow-hidden">
        <CardContent className="p-0 flex justify-center bg-gray-50 min-h-[400px]">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            }
            error={
              <div className="flex items-center justify-center py-16 text-red-500 text-sm">
                Failed to render PDF
              </div>
            }
          >
            <Page
              pageNumber={currentPage}
              width={750}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage(p => p - 1)}
          data-testid="pdf-prev-page"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />Previous
        </Button>
        <span className="text-sm text-muted-foreground" data-testid="pdf-page-indicator">
          Page {currentPage} / {numPages}
        </span>
        <Button
          variant="outline"
          disabled={currentPage >= numPages}
          onClick={() => setCurrentPage(p => p + 1)}
          data-testid="pdf-next-page"
        >
          Next<ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export const PdfUploadControl = ({ code, pdfSlides, onPdfChange }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File must be under 50MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/training/modules/${code}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success('PDF uploaded successfully');
      onPdfChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!confirm('Remove PDF slides? This will revert to text slides.')) return;
    setDeleting(true);
    try {
      await api.delete(`/training/modules/${code}/pdf`);
      toast.success('PDF removed');
      onPdfChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="pdf-upload-control">
      <label className="cursor-pointer">
        <input
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleUpload}
          disabled={uploading}
          data-testid="pdf-file-input"
        />
        <Button
          variant="outline"
          size="sm"
          className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
          disabled={uploading}
          asChild
        >
          <span>
            {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            {uploading ? 'Uploading...' : (pdfSlides ? 'Replace PDF' : 'Upload PDF')}
          </span>
        </Button>
      </label>
      {pdfSlides && (
        <>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <FileText className="w-3 h-3" />{pdfSlides.filename}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
            onClick={handleDelete}
            disabled={deleting}
            data-testid="pdf-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </>
      )}
    </div>
  );
};
